import { FormattedMessage, useIntl } from 'react-intl'
import Head from 'next/head'

import { Button, ContentStack, Body, Headline, FieldSet } from '@gaia-family/ds'

import { Screen } from '../../components/Layout'

import { Fields, FormManager } from '@components/forms'
import { useLocalStorage } from '@hooks/useLocalStorage'
import { FormikValues } from 'formik'
import { useRouter } from 'next/router'
import { Routes } from '@hooks/useRouting'
import * as z from 'zod'
import { toFormikValidationSchema } from '@gaia-family/dom'
import { TrimmedString } from '@gaia-family/lib/src/validation'
import { useEffect, useState } from 'react'

const Welcome = () => {
  const { formatMessage } = useIntl()
  const [_firstName, setFirstName] = useLocalStorage('firstName')
  const [_lastName, setLastName] = useLocalStorage('lastName')
  const router = useRouter()
  const saveName = async (values: FormikValues) => {
    const parsedValues = validationSchema.parse(values)
    await setFirstName(parsedValues.firstName)
    await setLastName(parsedValues.lastName)
    await router.push(Routes.welcomePartner)
  }

  const [jsLoaded, setJsLoaded] = useState(false)
  useEffect(() => {
    setJsLoaded(true)
  }, [])

  const validationSchema = z.object({
    firstName: TrimmedString(
      formatMessage({
        defaultMessage: 'First name cannot be empty',
      })
    ),
    lastName: TrimmedString(
      formatMessage({
        defaultMessage: 'Last name cannot be empty',
      })
    ),
  })
  return (
    <Screen minHeight={['60vh', null, '80vh']} py={['xl', 'xxxl']}>
      <Head>
        <title>
          {formatMessage({
            defaultMessage: 'Welcome - Gaia',
          })}
        </title>
      </Head>
      <ContentStack
        data-cy="welcome-content"
        alignItems="center"
        textAlign="center"
        maxWidth="article"
      >
        <Headline fontSize="xxl" size="xxl">
          <FormattedMessage defaultMessage="What's your name?" />
        </Headline>
        <Body color="softBlack">
          <FormattedMessage
            defaultMessage="It'll take you 3 minutes to complete your fertility profile. {br}You can always come back to update it. {br}But first, let's get to know you."
            values={{ br: <br /> }}
          />
        </Body>
        <FormManager
          width={['full', 'summary']}
          initialValues={{
            firstName: '',
            lastName: '',
          }}
          onSubmit={saveName}
          validationSchema={toFormikValidationSchema(validationSchema)}
        >
          {({ isSubmitting }) => {
            return (
              <FieldSet width="100%">
                <Fields.Text
                  wrapProps={{ flex: 1 }}
                  name="firstName"
                  placeholder="First name"
                  data-cy="first-name"
                />
                <Fields.Text
                  wrapProps={{ flex: 1 }}
                  name="lastName"
                  placeholder="Last name"
                  data-cy="last-name"
                />
                <Button
                  type="submit"
                  size="lg"
                  data-cy="first-last-name-submit"
                  isDisabled={!jsLoaded || isSubmitting}
                  variant="tertiary"
                  width="fit-content"
                  alignSelf="center"
                >
                  <ButtonLabel
                    jsLoaded={jsLoaded}
                    isSubmitting={isSubmitting}
                  />
                </Button>
              </FieldSet>
            )
          }}
        </FormManager>
      </ContentStack>
    </Screen>
  )
}

const ButtonLabel = ({
  jsLoaded,
  isSubmitting,
}: {
  jsLoaded: boolean
  isSubmitting: boolean
}) => {
  if (!jsLoaded) return <FormattedMessage defaultMessage="Page loading..." />
  if (isSubmitting) return <FormattedMessage defaultMessage="Submitting..." />
  return <FormattedMessage defaultMessage="Next" />
}

export default Welcome
